import React, { useMemo } from 'react';
import { View, StyleSheet, TouchableOpacity } from 'react-native';
import { IActivity, WorkpadConfig, safeWorkpadConfig } from '@eduwrite/shared';
import { useWorkpadStore } from '../store/useWorkpadStore';
import { useFeatureFlags } from '../hooks/useFeatureFlags';
import { ActivityWorkpadProvider } from '../hooks/useActivityWorkpad';
import { WorkpadSheet } from './WorkpadSheet';

interface WorkpadHostProps {
    activity: IActivity;
    children: React.ReactNode;
}

export const WorkpadHost: React.FC<WorkpadHostProps> = ({ activity, children }) => {
    const flags = useFeatureFlags();
    const { open: openWorkpad, isOpen } = useWorkpadStore();
    const rawConfig = (activity.config as Record<string, unknown>)?.workpad;
    const config: WorkpadConfig = useMemo(() => safeWorkpadConfig(rawConfig), [rawConfig]);

    // WP-028: render WorkpadSheet when workpad flag is on AND either the
    // activity-level config is enabled OR the sheet was opened programmatically
    // (e.g. by a choice.guidedAssist scaffold). The FAB only shows for
    // activity-level enabled configs so dynamic scaffolds don't add a stray button.
    const workpadEnabled = flags.workpad && (config.enabled || isOpen);
    const showFab = flags.workpad && config.enabled;

    const contextValue = useMemo(
        () => ({
            config,
            isOpen,
            openManually: () => {
                if (config.enabled && config.mode !== 'none') {
                    openWorkpad(config);
                }
            },
            requestScaffoldOnIncorrect: (): boolean => {
                if (workpadEnabled && config.enabled && config.mode === 'guided_steps') {
                    openWorkpad(config);
                    return true;
                }
                return false;
            },
        }),
        [config, isOpen, openWorkpad, workpadEnabled],
    );

    return (
        <ActivityWorkpadProvider value={contextValue}>
            <View style={styles.container}>
                {children}

                {workpadEnabled && (
                    <>
                        {/* Floating action button — only for activity-level workpad configs */}
                        {showFab && (
                            <TouchableOpacity
                                style={styles.fab}
                                onPress={contextValue.openManually}
                                accessibilityLabel="Open workpad"
                                accessibilityRole="button"
                                accessibilityHint="Double tap to open the workpad for working out"
                                testID="workpad-fab"
                            >
                                <View style={styles.fabInner} />
                            </TouchableOpacity>
                        )}

                        {/* Workpad sheet */}
                        <WorkpadSheet config={config} />
                    </>
                )}
            </View>
        </ActivityWorkpadProvider>
    );
};

const styles = StyleSheet.create({
    container: {
        flex: 1,
        minHeight: 0,
    },
    fab: {
        position: 'absolute',
        bottom: 24,
        right: 24,
        width: 56,
        height: 56,
        borderRadius: 28,
        backgroundColor: '#22c55e',
        justifyContent: 'center',
        alignItems: 'center',
        shadowColor: '#22c55e',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.3,
        shadowRadius: 8,
        elevation: 8,
        zIndex: 100,
    },
    fabInner: {
        width: 24,
        height: 24,
        borderRadius: 12,
        backgroundColor: '#FFFFFF',
        opacity: 0.9,
    },
});
